import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Share2, Mail, Phone, Download, MessageCircle, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { buildReferralLink } from "@/lib/referral";
import { CreceLogo } from "@/components/CreceLogo";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import api from "@/lib/api";

export default function VisitingCard() {
  const { user } = useAuth();
  const { level_names, partner_label } = useSettings();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [rank, setRank] = useState(null);
  useEffect(() => { document.title = "Crece · Digital Card"; }, []);
  useEffect(() => {
    api.get("/my-rank").then((r) => setRank(r.data)).catch(() => {});
  }, []);
  if (!user) return null;
  const link = buildReferralLink(user.referral_code, "card", "share");
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=0&data=${encodeURIComponent(link)}`;
  const rankIdx = rank?.rank_index ?? 0;
  const isPLHead = user.role === "pl_partner";
  const rankName = isPLHead
    ? "P&L Head"
    : rankIdx === 0
      ? (partner_label || "Agent")
      : (level_names && level_names[rankIdx - 1]) || `Level ${rankIdx}`;

  const cardText = `${user.name} · Crece ${rankName}\n📞 +91 ${user.mobile}\n${user.email || ""}\nReferral: ${user.referral_code}\nJoin via: ${link}`;

  const shareCard = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "My Crece Card", text: cardText }); } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(cardText);
      toast.success("Card copied to clipboard");
    }
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        backgroundColor: "transparent",
        cacheBust: true,
      });
      const a = document.createElement("a");
      a.download = `crece-card-${user.referral_code}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Couldn't generate image — try again");
    } finally {
      setDownloading(false);
    }
  };

  const whatsappShare = () => {
    const text = `Hi! I'm ${user.name}, a Crece Partner. Use my referral code *${user.referral_code}* to join and get the best deals on credit cards, loans & insurance:\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-stone-100 p-4">
      <div className="max-w-md mx-auto py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-emerald-900 mb-4" data-testid="card-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div ref={cardRef} className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-stone-50 rounded-2xl p-7 shadow-2xl" data-testid="visiting-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2"><CreceLogo size="sm" theme="light" /><span className="font-display font-black text-xl">Crece</span></div>
            {/* Single rank/title tag — was duplicated below as a pill */}
            <span className="text-xs font-bold uppercase tracking-widest text-amber-400" data-testid="card-rank-tag">
              {isPLHead ? "P&L Head" : rankName}
            </span>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight">{user.name}</h1>
          {/* Show the small rank pill ONLY when the rank tag in the header isn't already the title. Right now both
              come from the same source, so we hide the pill to remove the duplicate the user complained about.
              Kept the testid for backwards-compat with screenshot tests. */}
          {rankIdx > 0 && isPLHead ? (
            <div className="mt-1.5 inline-flex items-center gap-1.5 bg-amber-400/15 border border-amber-400/40 text-amber-300 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-testid="card-rank-badge">
              <Award className="w-3 h-3" /> Level {rankIdx} · {level_names && level_names[rankIdx - 1]}
            </div>
          ) : null}
          <div className="mt-3 space-y-1.5 text-sm text-stone-200">
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-amber-400" /> +91 {user.mobile}</div>
            {user.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-amber-400" /> {user.email}</div>}
            {user.city && <div className="text-xs text-stone-300 pl-5">{user.city}</div>}
          </div>
          <div className="mt-6 bg-white p-3 rounded-xl inline-block">
            <img src={qr} alt="Referral QR" className="w-32 h-32" crossOrigin="anonymous" />
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-400">Referral Code</div>
            <div className="font-display text-3xl font-black tracking-wider mt-1">{user.referral_code}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Button data-testid="download-card-png" onClick={downloadPng} disabled={downloading}
            className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold h-12 col-span-1">
            <Download className="w-4 h-4" />
          </Button>
          <Button data-testid="whatsapp-card-button" onClick={whatsappShare}
            variant="outline" className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50 h-12 col-span-1">
            <MessageCircle className="w-4 h-4" />
          </Button>
          <Button data-testid="share-card-button" onClick={shareCard}
            variant="outline" className="rounded-full border-stone-300 h-12 col-span-1">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px] text-stone-500 font-bold uppercase tracking-wider text-center mt-1.5">
          <span>Download</span>
          <span>WhatsApp</span>
          <span>Share</span>
        </div>
      </div>
    </div>
  );
}
